import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, resolveImageUrl } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  DollarSign, Users, Bell, TrendingUp, ArrowRight, Plus, Minus, Copy, Check,
  ExternalLink, Star, Zap, X, Sparkles, MapPin, Briefcase, Clock, Heart,
  CheckCircle2, MessageCircle, Send, Trash2, HelpCircle, Lightbulb, PenLine,
  Share2, UserPlus, Calendar, BookOpen, Search, ChevronRight, Target, Edit3, Video,
  Rocket, Network, Award, QrCode, Download, Chrome, Puzzle, Bot, ChevronDown, Store,
  Gift, Mail, Link2, Loader2, Megaphone, FileText, Upload, Trophy
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { useFoundingStatus } from '../hooks/useFoundingStatus';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import FeedbackModal from '../components/FeedbackModal';
import ContactImportModal from '../components/ContactImportModal';
import { ActivateModal } from '../components/PremiumGate';

// ==================== CONSTANTS ====================
const POST_TYPES = [
  { id: 'update', label: 'Update', icon: PenLine, color: 'bg-blue-100 text-blue-700' },
  { id: 'promotion', label: 'Promotion', icon: TrendingUp, color: 'bg-green-100 text-green-700' },
  { id: 'question', label: 'Question', icon: HelpCircle, color: 'bg-amber-100 text-amber-700' },
  { id: 'tip', label: 'Pro Tip', icon: Lightbulb, color: 'bg-purple-100 text-purple-700' },
  { id: 'event_promotion', label: 'Event', icon: Calendar, color: 'bg-rose-100 text-rose-700' },
];

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};

// Profession → recommended networking partner map
const PROFESSION_PARTNER_MAP = {
  real_estate_agent: [
    { title: 'Mortgage Loan Officer', reason: 'Every buyer needs financing — create a seamless pipeline' },
    { title: 'Home Inspector', reason: 'Build a trusted team for every transaction' },
    { title: 'Title Company', reason: 'Smooth closings and mutual referrals on every deal' },
  ],
  loan_officer: [
    { title: 'Realtor', reason: 'The #1 source of purchase referrals for loan officers' },
    { title: 'Financial Advisor', reason: 'Help clients plan for home ownership and retirement together' },
    { title: 'Insurance Agent', reason: 'Buyers need homeowners insurance — perfect warm referral' },
  ],
  insurance_agent: [
    { title: 'Loan Officer', reason: 'Every mortgage requires insurance — natural referral partner' },
    { title: 'Realtor', reason: 'New homeowners need all lines of insurance coverage' },
    { title: 'Accountant', reason: 'Shared clients who need both financial and insurance services' },
  ],
  attorney: [
    { title: 'Accountant', reason: 'Shared clients needing legal and tax planning together' },
    { title: 'Financial Advisor', reason: 'Estate planning, business formation — perfect overlap' },
    { title: 'Real Estate Agent', reason: 'Real estate transactions need legal guidance' },
  ],
  financial_advisor: [
    { title: 'Accountant', reason: 'Tax-efficient investing strategies for shared clients' },
    { title: 'Attorney', reason: 'Estate plans, trusts, and wealth protection' },
    { title: 'Insurance Agent', reason: 'Complete financial plan requires insurance coverage' },
  ],
  accountant: [
    { title: 'Financial Advisor', reason: 'Complete financial picture — tax + investment strategies' },
    { title: 'Attorney', reason: 'Business formation and legal + tax planning' },
    { title: 'Insurance Agent', reason: 'Business and personal insurance for shared clients' },
  ],
  business_consultant: [
    { title: 'Accountant', reason: 'Business growth requires solid financial management' },
    { title: 'Marketing Consultant', reason: 'Operations + marketing = complete business solutions' },
    { title: 'Financial Advisor', reason: 'Business owners need financial planning and growth strategy' },
  ],
  marketing_consultant: [
    { title: 'Web Developer', reason: 'Every marketing strategy needs a great website' },
    { title: 'Business Consultant', reason: 'Combine business strategy with marketing execution' },
    { title: 'Graphic Designer', reason: 'Complete brand and marketing package for clients' },
  ],
  contractor: [
    { title: 'Real Estate Agent', reason: 'Investors and flippers need reliable contractors' },
    { title: 'Architect', reason: 'New builds and major renovations need both skills' },
    { title: 'Interior Designer', reason: 'Complete renovation from structure to design' },
  ],
};

const getPartnerSuggestions = (professionId) => {
  return PROFESSION_PARTNER_MAP[professionId] || [
    { title: 'Accountant', reason: 'Most professionals benefit from a strong financial partner' },
    { title: 'Marketing Consultant', reason: 'Grow your business with targeted marketing' },
    { title: 'Attorney', reason: 'Protect your business with proper legal guidance' },
  ];
};

// ==================== NETWORKING COACH MODAL (AI CHAT) ====================
const NetworkingCoachModal = ({ user, onClose, recommended = [] }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('preferences'); // 'preferences' | 'chat'
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [sessionId] = useState(() => `coach-${user?.id}-${Date.now()}`);
  const [matchedPerson, setMatchedPerson] = useState(null);
  const [showActivateCoach, setShowActivateCoach] = useState(false);
  const messagesEndRef = useRef(null);

  const professionName = user?.profession_name || user?.custom_profession ||
    (user?.profession_id ? user.profession_id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'your profession');
  const partners = getPartnerSuggestions(user?.profession_id);

  useEffect(() => {
    setMessages([{
      role: 'ai',
      content: `Hi ${user?.full_name?.split(' ')[0] || 'there'}! I'm your AI Networking Coach.\n\nWhat are your preferred networking partner preferences?\n\nBased on your profile as a **${professionName}**, here are my top recommended matches for you:`
    }]);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Find a real person from the recommended list that matches selected/chosen partner type
  const findMatchedPerson = (partnerTitle) => {
    if (!recommended.length) return null;
    const keyword = partnerTitle.toLowerCase();
    return recommended.find(r => {
      const prof = (r.owner?.profession || '').toLowerCase();
      return prof.includes(keyword.split(' ')[0]) || keyword.includes(prof.split(' ')[0]);
    }) || recommended[0];
  };

  const sendMessage = async (msg, skipUserMessage = false) => {
    if (!msg.trim() || loading) return;
    if (!skipUserMessage) setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    setStep('chat');
    try {
      const res = await api.coachChat(sessionId, msg);
      if (res.daily_limit_reached) {
        setMessages(prev => [...prev, { role: 'ai', content: res.message, isLimit: true, requiresActivation: res.requires_activation || false }]);
      } else {
        const match = findMatchedPerson(selectedPartners[0] || msg);
        setMatchedPerson(match || null);
        setMessages(prev => [...prev, { role: 'ai', content: res.message, match, remaining: res.messages_remaining }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'ai', content: 'Great choices! Start by reaching out to 1-2 of these professionals this week with a personalized message. What aspect of networking would you like help with?' }]);
    } finally { setLoading(false); }
  };

  const handleSelectPartner = (title) => {
    setSelectedPartners(prev =>
      prev.includes(title) ? prev.filter(p => p !== title) : [...prev, title]
    );
  };

  const handleConfirmPreferences = () => {
    if (selectedPartners.length === 0) { toast.error('Select at least one partner type'); return; }
    const msg = `My preferred networking partners are: ${selectedPartners.join(', ')}. I'm a ${professionName} in ${user?.city || 'my area'}. Give me a specific coaching plan and recommend a connection.`;
    setMessages(prev => [...prev, { role: 'user', content: `I want to focus on networking with: ${selectedPartners.join(', ')}` }]);
    sendMessage(msg, true);
  };

  const handleSend = () => {
    const msg = input.trim();
    if (!msg) return;
    setInput('');
    sendMessage(msg);
  };

  const formatMessage = (text) => text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>');

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background rounded-2xl shadow-2xl w-full max-w-lg flex flex-col border border-border/50"
        style={{ height: '600px' }}
        onClick={e => e.stopPropagation()}
        data-testid="coach-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border/50 bg-gradient-to-r from-primary/5 to-transparent rounded-t-2xl">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
              <Bot className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold text-sm">AI Networking Coach</p>
              <p className="text-[10px] text-green-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full inline-block" />
                Online — powered by GPT
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-lg transition-colors">
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((msg, i) => (
            <div key={i}>
              <div className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {msg.role === 'ai' && (
                  <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    msg.role === 'ai'
                      ? 'bg-muted/60 text-foreground rounded-tl-none'
                      : 'bg-primary text-primary-foreground rounded-tr-none'
                  }`}
                  dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                />
              </div>
              {/* Daily limit message */}
              {msg.role === 'ai' && msg.requiresActivation && (
                <div className="ml-9 mt-2 p-3 rounded-xl bg-blue-50 border border-blue-200 text-center">
                  <p className="text-xs text-blue-700">You've reached your daily AI Coach limit. Come back tomorrow!</p>
                </div>
              )}
              {msg.role === 'ai' && msg.match && (
                <div className="ml-9 mt-2 p-3 rounded-xl border-2 border-primary/30 bg-primary/5" data-testid="coach-match-card">
                  <p className="text-[10px] font-semibold text-primary uppercase tracking-wide mb-2">Recommended Connection</p>
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden bg-gradient-to-br from-primary/30 to-primary">
                      {msg.match.owner?.profile_photo ? (
                        <img src={resolveImageUrl(msg.match.owner.profile_photo)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="w-full h-full flex items-center justify-center text-white font-bold text-sm">
                          {(msg.match.owner?.name || 'U')[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{msg.match.owner?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{msg.match.owner?.profession} · {msg.match.owner?.city}</p>
                    </div>
                    <Link to={`/network`} onClick={onClose}>
                      <Button size="sm" className="text-xs h-7 px-3 flex-shrink-0" data-testid="coach-connect-btn">
                        Connect
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Partner Selection Cards (step = preferences) */}
          {step === 'preferences' && messages.length > 0 && (
            <div className="space-y-2 pl-9">
              {partners.map((p, i) => (
                <button
                  key={i}
                  onClick={() => handleSelectPartner(p.title)}
                  className={`w-full text-left p-3 rounded-xl border-2 transition-all text-sm ${
                    selectedPartners.includes(p.title)
                      ? 'border-primary bg-primary/10 shadow-sm'
                      : 'border-border/50 hover:border-primary/40 bg-muted/30'
                  }`}
                  data-testid={`partner-option-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
                      selectedPartners.includes(p.title) ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                    }`}>
                      {selectedPartners.includes(p.title) && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <span className="font-medium">{p.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 pl-6">{p.reason}</p>
                </button>
              ))}
              {/* AI Custom */}
              <button
                onClick={() => handleSelectPartner('AI Custom Recommendation')}
                className={`w-full text-left p-3 rounded-xl border-2 transition-all text-sm bg-gradient-to-r ${
                  selectedPartners.includes('AI Custom Recommendation')
                    ? 'border-primary from-primary/10 to-purple-500/10 shadow-sm'
                    : 'border-dashed border-primary/30 from-primary/5 to-purple-500/5 hover:border-primary/50'
                }`}
                data-testid="ai-custom-option"
              >
                <div className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                    selectedPartners.includes('AI Custom Recommendation') ? 'border-primary bg-primary' : 'border-primary/40'
                  }`}>
                    {selectedPartners.includes('AI Custom Recommendation') && <Check className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <span className="font-medium text-primary">AI Custom Recommendation</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 pl-6">Let AI suggest the best match based on your full profile</p>
              </button>

              {selectedPartners.length > 0 && (
                <Button
                  className="w-full mt-2"
                  onClick={handleConfirmPreferences}
                  disabled={loading}
                  data-testid="confirm-preferences-btn"
                >
                  {loading ? 'Analyzing...' : (
                    <><Sparkles className="w-4 h-4 mr-2" />Get My Coaching Plan</>
                  )}
                </Button>
              )}
            </div>
          )}

          {loading && (
            <div className="flex gap-2.5 pl-9">
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="bg-muted/60 rounded-2xl rounded-tl-none px-3.5 py-2.5">
                <div className="flex gap-1 items-center h-5">
                  <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-muted-foreground/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Always-visible chat input */}
        <div className="p-4 border-t border-border/50">
          {step === 'preferences' && (
            <p className="text-[10px] text-muted-foreground mb-2 text-center">Select an option above or type your own preference below</p>
          )}
          <div className="flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={step === 'preferences' ? 'Type your own preference...' : 'Ask your coach anything...'}
              className="flex-1 bg-muted/40 border border-border rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="coach-chat-input"
              disabled={loading}
            />
            <Button
              size="icon"
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="rounded-full w-9 h-9 flex-shrink-0"
              data-testid="coach-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== PROFILE COMPLETION TRACKER ====================
const ProfileTracker = ({ user, profileViews }) => {
  // Calculate profile completion percentage
  const fields = [
    !!user?.full_name,
    !!user?.profession_id,
    !!user?.company,
    !!user?.phone,
    !!user?.website,
    !!user?.profile_photo_url,
    !!user?.city,
    !!user?.linkedin_url || !!user?.twitter_url,
  ];
  const completed = fields.filter(Boolean).length;
  const percentage = Math.round((completed / fields.length) * 100);

  return (
    <div className="space-y-3" data-testid="profile-tracker">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs font-medium text-green-600">{percentage}%</span>
      </div>
      <p className="text-xs text-muted-foreground">
        Your profile is <span className="font-medium text-green-600">{percentage}% referral ready</span>
      </p>
    </div>
  );
};

// ==================== QUICK LINKS ====================
const QuickLinks = ({ user, onFeedback }) => {
  const links = [
    { icon: Store, label: 'Marketplace', desc: 'Browse ad marketplace', href: '/marketplace', color: 'green' },
    { icon: Rocket, label: 'Jumpstart', desc: 'Discover professionals', href: '/explore' },
    { icon: MapPin, label: user?.city || 'Your City', desc: 'Your community', href: '/explore' },
    { icon: HelpCircle, label: 'Get Support', action: 'feedback' },
    { icon: BookOpen, label: 'Product Tutorials', desc: 'Quick walkthroughs', href: '/tutorials' },
    { icon: UserPlus, label: 'Invite Business Owners', href: '/explore' },
    { icon: PenLine, label: 'Create Signature', desc: 'Build your email signature', href: '/signatures', highlight: true },
  ];

  return (
    <div className="space-y-1" data-testid="quick-links">
      <h3 className="text-sm font-semibold mb-2">Quick Links</h3>
      {links.map((link, i) => (
        link.highlight ? (
          <Link
            key={i}
            to={link.href}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 transition-colors group mt-1"
            data-testid={`quick-link-${i}`}
          >
            <link.icon className="w-4 h-4 text-white" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">{link.label}</p>
              {link.desc && <p className="text-[10px] text-white/80">{link.desc}</p>}
            </div>
          </Link>
        ) : link.color === 'green' ? (
          <Link
            key={i}
            to={link.href}
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 transition-colors group"
            data-testid={`quick-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <link.icon className="w-4 h-4 text-white" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">{link.label}</p>
              {link.desc && <p className="text-[10px] text-white/80">{link.desc}</p>}
            </div>
          </Link>
        ) : link.action === 'feedback' ? (
          <button
            key={i}
            onClick={onFeedback}
            className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted/50 transition-colors group w-full text-left"
            data-testid={`quick-link-${i}`}
          >
            <link.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{link.label}</p>
            </div>
          </button>
        ) : (
          <Link
            key={i}
            to={link.href}
            className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted/50 transition-colors group"
            data-testid={`quick-link-${i}`}
          >
            <link.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{link.label}</p>
              {link.desc && <p className="text-[10px] text-muted-foreground">{link.desc}</p>}
            </div>
          </Link>
        )
      ))}
    </div>
  );
};

// ==================== MY GROUPS WIDGET ====================
const MyGroupsWidget = ({ groups }) => {
  return (
    <div className="space-y-2 mt-4 pt-4 border-t border-border/50" data-testid="my-groups-widget">
      <h3 className="text-sm font-semibold">My Groups</h3>
      {groups.slice(0, 3).map((group) => (
        <Link
          key={group.id}
          to={`/groups`}
          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/50 transition-colors"
          data-testid={`group-link-${group.id}`}
        >
          <MapPin className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-medium flex-1 truncate">{group.name}</span>
          {group.unread_count > 0 && (
            <span className="w-2 h-2 bg-red-500 rounded-full" />
          )}
        </Link>
      ))}
      <Link to="/groups" className="flex items-center gap-1.5 text-xs text-primary hover:underline px-2">
        <Search className="w-3 h-3" />
        Browse Groups
      </Link>
    </div>
  );
};

// ==================== AD PLACEMENT PREVIEW ====================
const AdPlacementPreview = ({ user }) => {
  return (
    <div className="mt-4 pt-4 border-t border-border/50" data-testid="ad-preview-widget">
      <div className="text-center mb-2">
        <h3 className="text-xs font-semibold">Get Business Owners As Customers</h3>
        <p className="text-[10px] text-muted-foreground">Advertise your business in {user?.city || 'your area'}</p>
      </div>
      <p className="text-[10px] text-muted-foreground text-center italic mb-2">Ad placement preview:</p>
      <div className="border border-border rounded-lg p-3 bg-background">
        <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-primary/30 to-primary/60 flex items-center justify-center mb-2">
          <span className="text-primary font-bold text-lg">
            {(user?.full_name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2)}
          </span>
        </div>
        <p className="text-xs font-semibold text-center">{user?.full_name}</p>
        <p className="text-[10px] text-muted-foreground text-center">{user?.company || 'Your Company'}</p>
        <p className="text-[10px] text-muted-foreground text-center flex items-center justify-center gap-1">
          <Briefcase className="w-2.5 h-2.5" />
          {user?.profession_name || 'Professional'}
        </p>
      </div>
      <Link to="/marketplace">
        <Button size="sm" className="w-full mt-3 bg-primary hover:bg-primary/90 text-xs h-8 animate-pulse shadow-lg shadow-primary/30" data-testid="advertise-now-btn">
          Advertise Now
        </Button>
      </Link>
    </div>
  );
};

// ==================== QUICK NETWORK HUB (Discover | Explore) ====================
const QuickNetworkHub = ({ user, recommended = [] }) => {
  const [activeTab, setActiveTab] = useState('discover');

  const tabs = [
    { id: 'discover', label: 'Discover', icon: Search },
    { id: 'smartconnect', label: 'Explore', icon: Network },
  ];

  return (
    <Card data-testid="quick-network-hub">
      <CardContent className="p-4">
        <div className="flex gap-1 mb-4 border-b border-border/50 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg whitespace-nowrap transition-all flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={`network-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'discover' && (
          <div>
            <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Search className="w-4 h-4 text-blue-500" />
                <p className="text-sm font-semibold">Find Your Next Connection</p>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Explore professionals matched to your networking goals in your area.
              </p>
            </div>
            <Link to="/explore">
              <Button size="sm" className="w-full text-xs h-8" data-testid="network-cta-discover">
                <Search className="w-3.5 h-3.5 mr-1.5" />
                Explore Matches
              </Button>
            </Link>
          </div>
        )}

        {activeTab === 'smartconnect' && (
          <div data-testid="smartconnect-panel">
            {recommended.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground mb-2 font-medium">Recommended for you</p>
                {recommended.slice(0, 3).map((rec) => (
                  <div key={rec.id} className="flex items-center gap-2.5 p-2.5 rounded-xl border border-border/50 hover:border-primary/40 hover:bg-muted/20 transition-all">
                    <div className="w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-gradient-to-br from-primary/30 to-primary">
                      {rec.owner?.profile_photo ? (
                        <img src={resolveImageUrl(rec.owner.profile_photo)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="w-full h-full flex items-center justify-center text-white font-bold text-sm">
                          {(rec.owner?.name || 'U')[0]}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{rec.owner?.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{rec.owner?.profession}</p>
                    </div>
                <Link to="/explore">
                  <Button size="sm" className="text-[10px] h-6 px-2 flex-shrink-0" data-testid={`sc-connect-${rec.id}`}>
                    Connect
                  </Button>
                </Link>
                  </div>
                ))}
                <Link to="/explore">
                  <Button variant="outline" size="sm" className="w-full text-xs h-7 mt-1" data-testid="sc-view-all">
                    View All Matches <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div>
                <div className="bg-primary/5 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Network className="w-4 h-4 text-primary" />
                    <p className="text-sm font-semibold">AI-Powered Outreach</p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Send AI-crafted messages to professionals most likely to refer you business.
                  </p>
                </div>
                <Link to="/explore">
                  <Button size="sm" className="w-full text-xs h-8" data-testid="network-cta-smartconnect">
                    <Network className="w-3.5 h-3.5 mr-1.5" />
                    Start Connecting
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}

      </CardContent>
    </Card>
  );
};

// ==================== TOOLS HUB (Library | QR Code | Install Extension) ====================
const ToolsHub = ({ user }) => {
  const [activeTab, setActiveTab] = useState('library');
  const cardUrl = `${window.location.origin}/card/${user?.id}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&bgcolor=ffffff&color=0f172a&qzone=1&data=${encodeURIComponent(cardUrl)}`;

  const tabs = [
    { id: 'library', label: 'Library', icon: BookOpen },
    { id: 'qrcode', label: 'QR Code', icon: QrCode },
    { id: 'extension', label: 'Install Extension', icon: Puzzle },
  ];

  return (
    <Card data-testid="tools-hub">
      <CardContent className="p-4">
        <div className="flex gap-1 mb-4 overflow-x-auto pb-1 border-b border-border/50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg whitespace-nowrap transition-all flex-shrink-0 ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={`tools-tab-${tab.id}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'library' && (
          <div>
            <h3 className="text-sm font-semibold mb-1">Learning Hub</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Discover articles, tips, and resources to grow your business
            </p>
            <div className="space-y-2">
              {[
                'How to Build a Referral Network',
                'Top Email Signature Strategies',
                'Networking Events Near You',
              ].map((title, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full flex-shrink-0" />
                  <p className="text-xs font-medium">{title}</p>
                </div>
              ))}
            </div>
            <Link to="/learn">
              <Button variant="outline" size="sm" className="w-full mt-3 text-xs h-8" data-testid="explore-learning">
                <Lightbulb className="w-3.5 h-3.5 mr-1" />
                Explore All Resources
              </Button>
            </Link>
          </div>
        )}

        {activeTab === 'qrcode' && (
          <div className="text-center" data-testid="qrcode-tab">
            <h3 className="text-sm font-semibold mb-1">Your Business Card QR</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Share your ConnectMail profile with a scan
            </p>
            <div className="flex justify-center mb-3">
              <div className="border-2 border-border rounded-xl p-2 bg-white inline-block">
                <img
                  src={qrUrl}
                  alt="QR Code"
                  className="w-32 h-32"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mb-3">
              Scan to open: <span className="text-primary font-medium">/card/{user?.id?.slice(0, 8)}...</span>
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs h-8"
                onClick={() => { navigator.clipboard?.writeText(cardUrl); toast.success('Link copied!'); }}
                data-testid="copy-card-link"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy Link
              </Button>
              <a href={qrUrl} download="connectmail-qr.png">
                <Button size="sm" className="text-xs h-8" data-testid="download-qr">
                  <Download className="w-3 h-3 mr-1" />
                  Save QR
                </Button>
              </a>
            </div>
          </div>
        )}

        {activeTab === 'extension' && (
          <div data-testid="extension-tab">
            <h3 className="text-sm font-semibold mb-1">Browser Extension</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Add your ConnectMail signature to Gmail and Outlook automatically
            </p>
            <div className="bg-muted/30 rounded-lg p-3 mb-3 space-y-1.5">
              <div className="flex items-center gap-2 text-xs">
                <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                <span>Auto-inserts your signature in every email</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                <span>Works with Gmail, Outlook, Yahoo Mail</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <Check className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                <span>Syncs with your ad slots automatically</span>
              </div>
            </div>
            <div className="space-y-2">
              <Button size="sm" className="w-full text-xs h-8 bg-blue-600 hover:bg-blue-700" data-testid="install-chrome-ext">
                <Chrome className="w-3.5 h-3.5 mr-1.5" />
                Add to Chrome
              </Button>
              <Button size="sm" variant="outline" className="w-full text-xs h-8" data-testid="install-firefox-ext">
                <Puzzle className="w-3.5 h-3.5 mr-1.5" />
                Add to Firefox
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground text-center mt-2">
              Free forever with your ConnectMail account
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== NETWORKING COACH ====================
const NetworkingCoach = ({ user, recommendations, onUnlock, onEditGoal }) => {
  const targetProfession = user?.target_businesses?.[0] || 'professionals';
  const userCity = user?.city || 'your area';
  const partners = getPartnerSuggestions(user?.profession_id);
  const topPartner = partners[0]?.title || targetProfession;

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="networking-coach">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold">Networking Coach</h3>
          </div>
          <button
            onClick={onEditGoal}
            className="text-xs text-primary hover:underline flex items-center gap-1 transition-colors"
            data-testid="edit-goal-btn"
          >
            <Edit3 className="w-3 h-3" />
            Start
          </button>
        </div>
        
        <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 mb-4">
          <p className="text-xs text-emerald-700 dark:text-emerald-300 text-center">
            Showing suggestions to help you{' '}
            <Link to="/explore" className="underline font-medium">
              meet {topPartner}s near {userCity}
            </Link>
          </p>
        </div>

        {recommendations.length > 0 ? (
          <div className="space-y-3">
            {recommendations.slice(0, 1).map((rec) => (
              <div key={rec.id} className="p-3 rounded-lg border border-border bg-background" data-testid={`coach-rec-${rec.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  {rec.owner?.profile_photo ? (
                    <img
                      src={resolveImageUrl(rec.owner.profile_photo)}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border-2 border-primary/20"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/30 to-primary flex items-center justify-center">
                      <span className="text-white font-bold">{(rec.owner?.name || 'U')[0]}</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="text-sm">
                      Connect with a nearby{' '}
                      <span className="text-primary font-medium">{rec.owner?.profession || topPartner}</span>{' '}
                      — ideal referral partner for {user?.profession_name || 'your business'}
                    </p>
                  </div>
                </div>
                <Link to="/explore" className="block w-full">
                  <Button
                    size="sm"
                    className="w-full bg-green-600 hover:bg-green-700 text-xs h-8"
                    data-testid={`unlock-btn-${rec.id}`}
                  >
                    <Network className="w-3.5 h-3.5 mr-1.5" />
                    Explore
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4">
            <Target className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-xs text-muted-foreground mb-3">Complete your profile to get personalized recommendations</p>
            <Button size="sm" variant="outline" className="text-xs" onClick={onEditGoal} data-testid="setup-coach-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              Set Up Your Coach
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== ONBOARDING CHECKLIST ====================
const OnboardingChecklist = ({ user, stats }) => {
  const steps = [
    { id: 'account', label: 'Create your account', completed: true, href: null },
    { id: 'business', label: 'Tell us about your business', completed: !!user?.company, href: '/settings' },
    { id: 'network', label: 'Connect with your contact', completed: (stats?.connections_count || 0) > 0, href: '/invite-contacts' },
    { id: 'customers', label: 'Find groups', completed: false, href: '/groups' },
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const progress = (completedCount / steps.length) * 100;

  return (
    <Card data-testid="onboarding-checklist">
      <CardContent className="p-4">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-20 h-20 flex-shrink-0">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" strokeWidth="8" />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#22c55e"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${progress * 2.83} 283`}
                transform="rotate(-90 50 50)"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold">Get Started on ConnectMail</h3>
            <p className="text-xs text-muted-foreground">{completedCount} of {steps.length} completed</p>
          </div>
        </div>

        <div className="space-y-1">
          {steps.map((step, i) => {
            const row = (
              <div className={`flex items-center gap-3 py-2.5 px-2 rounded-lg border-b border-border/30 last:border-0 transition-colors ${
                step.href && !step.completed ? 'hover:bg-muted/50 cursor-pointer' : ''
              }`} data-testid={`checklist-step-${step.id}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                  step.completed ? 'bg-green-500' : 'border-2 border-muted-foreground/30'
                }`}>
                  {step.completed && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className={`flex-1 text-xs ${step.completed ? 'line-through text-muted-foreground' : 'font-medium'}`}>
                  {i + 1}. {step.label}
                </span>
                {step.href && !step.completed && (
                  <ChevronRight className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </div>
            );
            return step.href && !step.completed ? (
              <Link key={step.id} to={step.href} className="block">
                {row}
              </Link>
            ) : (
              <div key={step.id}>{row}</div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== FEED POST COMPOSER ====================
const PostComposer = ({ user, onPost }) => {
  const [content, setContent] = useState('');
  const [postType, setPostType] = useState('update');
  const [posting, setPosting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      await onPost(content, postType);
      setContent('');
      toast.success('Post shared!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Card data-testid="post-composer">
      <CardContent className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center flex-shrink-0 overflow-hidden">
            {user?.profile_photo_url ? (
              <img src={resolveImageUrl(user.profile_photo_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold">{(user?.full_name || 'U')[0]}</span>
            )}
          </div>
          <input
            type="text"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Post to Your Network"
            className="flex-1 bg-muted/30 border border-border rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="post-input"
          />
        </div>
        
        <div className="flex items-center justify-between border-t border-border/50 pt-3">
          <div className="flex gap-2">
            {POST_TYPES.slice(0, 2).map((type) => (
              <button
                key={type.id}
                onClick={() => setPostType(type.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  postType === type.id
                    ? type.color + ' ring-2 ring-offset-1 ring-primary/30'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`}
                data-testid={`post-type-${type.id}`}
              >
                <type.icon className="w-3.5 h-3.5" />
                {type.label}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={posting || !content.trim()}
            className="h-8"
            data-testid="submit-post-btn"
          >
            <Send className="w-3.5 h-3.5 mr-1" />
            {posting ? 'Posting...' : 'Post'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== FEED POST CARD ====================
const FeedPostCard = ({ post, user, onLike, onComment, onDelete }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [shared, setShared] = useState(false);

  const typeInfo = POST_TYPES.find(t => t.id === post.post_type) || POST_TYPES[0];
  const likesArray = Array.isArray(post.likes) ? post.likes : [];
  const isLiked = likesArray.includes(user?.id);
  const isOwn = post.user_id === user?.id;

  const loadComments = async () => {
    if (comments.length > 0) return;
    setLoadingComments(true);
    try {
      const data = await api.getComments(post.id);
      setComments(data.comments || []);
    } catch {}
    setLoadingComments(false);
  };

  const handleToggleComments = () => {
    setShowComments(!showComments);
    if (!showComments) loadComments();
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    try {
      const newComment = await api.addComment(post.id, commentText);
      setComments([...comments, newComment]);
      setCommentText('');
      onComment(post.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to comment');
    }
  };

  const handleShare = () => {
    const url = `${window.location.origin}/card/${post.user_id}`;
    if (navigator.share) {
      navigator.share({ title: `Post by ${post.author_name}`, text: post.content?.slice(0, 100), url });
    } else {
      navigator.clipboard?.writeText(url);
      toast.success('Link copied!');
    }
    setShared(true);
    setTimeout(() => setShared(false), 2000);
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`feed-post-${post.id}`}>
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 pb-2">
          <Link to={`/card/${post.user_id}`}>
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center overflow-hidden ring-2 ring-background shadow-sm">
              {post.author_photo ? (
                <img src={resolveImageUrl(post.author_photo)} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-bold">{(post.author_name || 'U')[0]}</span>
              )}
            </div>
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={`/card/${post.user_id}`} className="text-sm font-semibold hover:text-primary transition-colors" data-testid={`post-author-${post.id}`}>
                {post.author_name}
              </Link>
              {post.is_hiring && <span className="inline-flex items-center text-[10px] font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">Hiring</span>}
              <span className="text-xs text-muted-foreground">at {post.author_company || 'ConnectMail'}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-0.5">
              {post.author_city && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{post.author_city}{post.author_state ? `, ${post.author_state}` : ''}</span>}
              <span>·</span>
              <span>{timeAgo(post.created_at)}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className={`text-[10px] ${typeInfo.color} border-0`}>
              <typeInfo.icon className="w-3 h-3 mr-1" />
              {typeInfo.label}
            </Badge>
            {isOwn && (
              <button onClick={() => onDelete(post.id)} className="text-muted-foreground hover:text-destructive p-1 rounded-full hover:bg-destructive/10 transition-colors" data-testid={`delete-post-${post.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-3">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{post.content}</p>
        </div>

        {/* Event Promotion Card */}
        {post.post_type === 'event_promotion' && post.event_data && (
          <div className="mx-4 mb-3 border border-rose-200 bg-gradient-to-br from-rose-50 to-orange-50 rounded-xl p-4" data-testid={`event-promo-card-${post.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm text-rose-900">{post.event_data.title}</h4>
                {post.event_data.description && (
                  <p className="text-xs text-rose-700/80 mt-0.5 line-clamp-2">{post.event_data.description}</p>
                )}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-rose-600">
                  {post.event_data.event_date && (
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{post.event_data.event_date}{post.event_data.event_time ? ` at ${post.event_data.event_time}` : ''}</span>
                  )}
                  {post.event_data.event_type && (
                    <span className="flex items-center gap-1">
                      {post.event_data.event_type === 'virtual' ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                      {post.event_data.event_type === 'virtual' ? 'Virtual' : post.event_data.location || 'In-Person'}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Link to="/events" className="block mt-3">
              <Button size="sm" className="w-full bg-rose-600 hover:bg-rose-700 text-white text-xs h-8" data-testid={`event-rsvp-btn-${post.id}`}>
                <Calendar className="w-3.5 h-3.5 mr-1.5" />View Event & RSVP
              </Button>
            </Link>
          </div>
        )}

        {/* Group Promotion Card */}
        {post.post_type === 'group_promotion' && post.group_data && (
          <div className="mx-4 mb-3 border border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl p-4" data-testid={`group-promo-card-${post.id}`}>
            <div className="flex items-start gap-3">
              {post.group_data.cover_image ? (
                <img src={resolveImageUrl(post.group_data.cover_image)} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm text-violet-900">{post.group_data.name}</h4>
                {post.group_data.description && (
                  <p className="text-xs text-violet-700/80 mt-0.5 line-clamp-2">{post.group_data.description}</p>
                )}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-violet-600">
                  {post.group_data.member_count != null && (
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" />{post.group_data.member_count} members</span>
                  )}
                  {post.group_data.location && (
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{post.group_data.location}</span>
                  )}
                  {post.group_data.group_type && (
                    <span className="flex items-center gap-1 capitalize"><Briefcase className="w-3 h-3" />{post.group_data.group_type}</span>
                  )}
                </div>
              </div>
            </div>
            <Link to={`/groups/${post.group_data.id || ''}`} className="block mt-3">
              <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700 text-white text-xs h-8" data-testid={`group-join-btn-${post.id}`}>
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />View & Join Group
              </Button>
            </Link>
          </div>
        )}

        {/* Engagement Stats */}
        {(likesArray.length > 0 || (post.comment_count || 0) > 0) && (
          <div className="flex items-center justify-between px-4 py-1.5 text-[11px] text-muted-foreground">
            {likesArray.length > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                  <Heart className="w-2.5 h-2.5 text-white fill-white" />
                </div>
                <span>{likesArray.length}</span>
              </div>
            )}
            {(post.comment_count || 0) > 0 && (
              <button onClick={handleToggleComments} className="hover:underline">
                {post.comment_count} comment{post.comment_count > 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}

        {/* Action Bar */}
        <div className="flex items-center border-t border-border/50 mx-2">
          <button
            onClick={() => onLike(post.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium transition-all rounded-lg m-0.5 ${
              isLiked ? 'text-red-500 bg-red-50 dark:bg-red-950/20' : 'text-muted-foreground hover:bg-muted/50'
            }`}
            data-testid={`like-btn-${post.id}`}
          >
            <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
            {isLiked ? 'Liked' : 'Like'}
          </button>
          <button
            onClick={handleToggleComments}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 rounded-lg m-0.5 transition-colors"
            data-testid={`comment-btn-${post.id}`}
          >
            <MessageCircle className="w-4 h-4" />
            Comment
          </button>
          <button
            onClick={handleShare}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium rounded-lg m-0.5 transition-colors ${
              shared ? 'text-green-500 bg-green-50 dark:bg-green-950/20' : 'text-muted-foreground hover:bg-muted/50'
            }`}
            data-testid={`share-btn-${post.id}`}
          >
            <Share2 className="w-4 h-4" />
            {shared ? 'Shared!' : 'Share'}
          </button>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="border-t border-border/50 bg-muted/20 px-4 py-3 space-y-3">
            {loadingComments ? (
              <p className="text-xs text-muted-foreground text-center">Loading comments...</p>
            ) : (
              <>
                {comments.map((c) => (
                  <div key={c.id} className="flex gap-2.5" data-testid={`comment-${c.id}`}>
                    <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-primary">{(c.author_name || 'U')[0]}</span>
                    </div>
                    <div className="flex-1 bg-muted/50 rounded-xl px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold">{c.author_name}</span>
                        <span className="text-[10px] text-muted-foreground">{timeAgo(c.created_at)}</span>
                      </div>
                      <p className="text-xs text-foreground/80 mt-0.5">{c.content}</p>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <div className="w-7 h-7 rounded-full bg-primary/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-primary">{(user?.full_name || 'U')[0]}</span>
                  </div>
                  <div className="flex-1 flex gap-2">
                    <input
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSubmitComment()}
                      placeholder="Write a comment..."
                      className="flex-1 text-xs border border-border rounded-full px-4 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/20"
                      data-testid={`comment-input-${post.id}`}
                    />
                    <Button size="sm" variant="ghost" onClick={handleSubmitComment} className="h-8 w-8 p-0 rounded-full">
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== EVENT CARD ====================
const EventCard = ({ event }) => {
  return (
    <Card data-testid={`event-card-${event.id}`} className="overflow-hidden">
      <CardContent className="p-0">
        <div className="p-3 border-b border-border/30 bg-muted/20">
          <p className="text-[10px] text-muted-foreground">Event shared in {event.location || 'ConnectMail'}</p>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            {event.creator_photo ? (
              <img src={resolveImageUrl(event.creator_photo)} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-xs font-bold text-primary">{(event.creator_name || 'U')[0]}</span>
              </div>
            )}
            <div>
              <p className="text-xs font-medium">{event.creator_name}</p>
              <p className="text-[10px] text-muted-foreground">{event.creator_profession}</p>
            </div>
          </div>
          <p className="text-sm mb-3">{event.description?.slice(0, 120)}...</p>
          
          {event.image_url && (
            <div className="rounded-lg overflow-hidden mb-3 border border-border">
              <img src={resolveImageUrl(event.image_url)} alt="" className="w-full h-32 object-cover" />
            </div>
          )}
          
          <div className="bg-muted/30 rounded-lg p-3 mb-3">
            <p className="text-xs text-primary font-medium">{event.event_date} at {event.event_time} {event.timezone}</p>
            <p className="text-sm font-semibold">{event.title}</p>
            <Badge variant="outline" className="text-[10px] mt-1">
              {event.event_type === 'virtual' ? 'VIRTUAL' : 'IN-PERSON'}
            </Badge>
          </div>
          
          <Link to="/events">
            <Button className="w-full bg-primary hover:bg-primary/90" data-testid={`register-event-${event.id}`}>
              Register
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== INVITE A PRO MODAL ====================
const InviteProModal = ({ onClose }) => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sentList, setSentList] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getReferralLink();
        setReferralData(data);
      } catch { toast.error('Failed to load referral info'); }
      setLoading(false);
    })();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(referralData?.referral_link || '');
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSendInvite = async () => {
    if (!emailInput.trim() || !emailInput.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSending(true);
    try {
      await api.importContacts([{ email: emailInput.trim(), name: nameInput.trim() }]);
      setSentList(prev => [...prev, emailInput.trim()]);
      toast.success(`Invitation sent to ${emailInput.trim()}`);
      setEmailInput('');
      setNameInput('');
      // Refresh stats
      const data = await api.getReferralLink();
      setReferralData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send invitation');
    }
    setSending(false);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join ConnectMailAgent',
        text: 'I\'m inviting you to join ConnectMailAgent — the business networking platform for professionals!',
        url: referralData?.referral_link || window.location.origin,
      });
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="invite-pro-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 p-5 rounded-t-2xl relative">
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white font-['Outfit']">Invite a Pro</h2>
              <p className="text-emerald-100 text-sm">Earn 1 free month for each referral that activates!</p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
            </div>
          ) : (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-emerald-50 rounded-xl">
                  <p className="text-2xl font-bold text-emerald-700">{referralData?.total_invited || 0}</p>
                  <p className="text-[10px] text-emerald-600 font-medium">Invited</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                  <p className="text-2xl font-bold text-blue-700">{referralData?.total_referrals || 0}</p>
                  <p className="text-[10px] text-blue-600 font-medium">Joined</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-xl">
                  <p className="text-2xl font-bold text-amber-700">{referralData?.active_free_months || 0}</p>
                  <p className="text-[10px] text-amber-600 font-medium">Free Months</p>
                </div>
              </div>

              {/* Referral Link Section */}
              <div>
                <label className="text-xs font-semibold text-gray-700 mb-1.5 block">Your Referral Link</label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 truncate">
                    <Link2 className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{referralData?.referral_link || '...'}</span>
                  </div>
                  <Button size="sm" variant={copied ? 'default' : 'outline'} className={`h-10 px-3 ${copied ? 'bg-emerald-600 text-white' : ''}`} onClick={handleCopyLink} data-testid="copy-referral-link">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700" onClick={handleShare} data-testid="share-referral-link">
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Email Invite Form */}
              <div className="border-t border-gray-100 pt-4">
                <label className="text-xs font-semibold text-gray-700 mb-2 block flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  Send an Email Invitation
                </label>
                <div className="space-y-2">
                  <Input
                    placeholder="Name (optional)"
                    value={nameInput}
                    onChange={e => setNameInput(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="invite-name-input"
                  />
                  <div className="flex gap-2">
                    <Input
                      placeholder="Email address"
                      type="email"
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSendInvite()}
                      className="flex-1 h-9 text-sm"
                      data-testid="invite-email-input"
                    />
                    <Button size="sm" className="h-9 bg-emerald-600 hover:bg-emerald-700" onClick={handleSendInvite} disabled={sending} data-testid="send-invite-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" />Send</>}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Recently Sent */}
              {sentList.length > 0 && (
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-emerald-800 mb-1.5">Just Invited</p>
                  {sentList.map((email, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>{email}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* How it works */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs font-semibold text-gray-700 mb-2">How Referrals Work</p>
                <div className="space-y-2">
                  {[
                    { step: '1', text: 'Share your link or send an email invite' },
                    { step: '2', text: 'Your contact registers and activates their account' },
                    { step: '3', text: 'You earn 1 free month of service automatically!' },
                  ].map(item => (
                    <div key={item.step} className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">{item.step}</div>
                      <p className="text-xs text-gray-600">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== FOUNDING MEMBER WELCOME POPUP (On Login) ====================
// New policy (Feb 2026): all first-1,000 users are auto-granted permanent
// founding access at signup. This popup CELEBRATES status (no invite gate).
const FoundingMemberPopup = ({ onClose, onInvite }) => {
  const [foundingData, setFoundingData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('connectmail_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/founding-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setFoundingData(await res.json());
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleDontShowAgain = () => {
    localStorage.setItem('connectmail_hide_founding_popup', 'true');
    onClose();
  };

  const isFounding = !!foundingData?.is_founding_member;
  const memberNumber = foundingData?.founding_member_number;
  const spotsLeft = foundingData?.spots_remaining || 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="founding-member-popup">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-500 p-6 rounded-t-2xl relative">
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors" data-testid="founding-popup-close">
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-white/95 rounded-2xl flex items-center justify-center mb-3 shadow-lg">
              <Trophy className="w-9 h-9 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-white font-['Outfit']" data-testid="founding-popup-title">
              {isFounding ? "🏆 You're a Founding Member!" : 'Founding Member Program'}
            </h2>
            <p className="text-amber-50 text-sm mt-1">
              {isFounding ? 'Premium access included for life — on the house.' : `${spotsLeft} of 1,000 free founding seats left`}
            </p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
            </div>
          ) : (
            <>
              {isFounding ? (
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 text-center">
                  <p className="text-sm font-bold text-amber-900">You're one of the first 1,000 members.</p>
                  {memberNumber && (
                    <p className="text-2xl font-extrabold text-amber-600 mt-1 font-['Outfit']" data-testid="founding-member-number">
                      Member #{memberNumber}
                    </p>
                  )}
                  <p className="text-xs text-amber-800 mt-2 leading-relaxed">
                    Your premium access is <strong>locked in forever</strong>. No subscription. No invite requirement. No downgrade — ever.
                  </p>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-amber-900 mb-1">The free founding window has closed.</p>
                  <p className="text-xs text-amber-700">
                    Founding seats are reserved for our first 1,000 members and granted automatically at sign-up.
                  </p>
                </div>
              )}

              {/* Benefits */}
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">What's included for life</p>
                <div className="space-y-2">
                  {[
                    { icon: Star, text: 'All 17 signature templates — including the 3 premium animated ones', color: 'text-amber-500' },
                    { icon: Zap, text: 'Free Pro access locked in for life', color: 'text-emerald-500' },
                    { icon: TrendingUp, text: 'Every future premium feature, automatically unlocked', color: 'text-blue-500' },
                    { icon: Award, text: 'Founding Member badge on your profile and signatures', color: 'text-purple-500' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <item.icon className={`w-4 h-4 ${item.color} flex-shrink-0`} />
                      <span className="text-xs text-gray-600">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <Button
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold h-11"
                onClick={() => { if (isFounding) { onClose(); } else { onInvite(); onClose(); } }}
                data-testid="founding-popup-cta-btn"
              >
                {isFounding ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Start Using Premium Features
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite Friends to ConnectMail
                  </>
                )}
              </Button>

              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={handleDontShowAgain}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="founding-dont-show-btn"
                >
                  Don't show this again
                </button>
                <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={onClose} data-testid="founding-popup-skip">
                  Close
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== ACTIVATION POPUP (Non-Subscribers) ====================
const ActivationPopup = ({ onClose, onActivate, onTestActivate }) => {
  const handleDontShowAgain = () => {
    localStorage.setItem('connectmail_hide_activation_popup', 'true');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="activation-popup">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 rounded-t-2xl relative">
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-['Outfit']">Activate Your Account</h2>
              <p className="text-amber-100 text-sm">Start earning from your email signature</p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Unlock all features and start earning revenue from your email signature ad placements.
          </p>

          <div className="space-y-2.5">
            {[
              { icon: DollarSign, text: 'Earn $10 per filled ad slot', color: 'text-green-600 bg-green-50' },
              { icon: Megaphone, text: 'Advertise in the marketplace — pick your slot', color: 'text-blue-600 bg-blue-50' },
              { icon: Users, text: 'Unlimited connections & referrals', color: 'text-violet-600 bg-violet-50' },
              { icon: Sparkles, text: 'AI-powered networking coach', color: 'text-amber-600 bg-amber-50' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}>
                  <item.icon className="w-4 h-4" />
                </div>
                <p className="text-sm text-gray-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-amber-700 font-['Outfit']">$20<span className="text-sm font-normal text-amber-600">/month</span></p>
            <p className="text-xs text-amber-600 mt-1">Cancel anytime. 2 ad slots included.</p>
          </div>

          <div className="space-y-2">
            <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white h-11 text-sm font-semibold" onClick={onActivate} data-testid="activate-account-popup-btn">
              <Zap className="w-4 h-4 mr-2" />
              Activate My Account
            </Button>
            {window.location.hostname.includes('preview.emergentagent.com') && (
            <Button className="w-full bg-green-600 hover:bg-green-700 text-white h-10 text-sm font-semibold" onClick={onTestActivate} data-testid="test-activate-popup-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Test Activate (No Charge)
            </Button>
            )}
            <Link to="/marketplace" className="block">
              <Button variant="outline" className="w-full h-10 text-sm" onClick={onClose} data-testid="browse-ads-popup-btn">
                <Store className="w-4 h-4 mr-2" />
                Book an Ad Slot First
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <button
              onClick={handleDontShowAgain}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="activation-dont-show-btn"
            >
              Don't show this again
            </button>
            <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={onClose} data-testid="activation-popup-skip">
              Maybe later
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== SIGNATURE UPDATE POPUP ====================
const SignatureUpdatePopup = ({ reason, onUpdate, onDismiss }) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onDismiss}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-testid="signature-update-popup">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-5 rounded-t-2xl relative">
          <button onClick={onDismiss} className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white font-['Outfit']">Hooray! New Advertiser</h2>
              <p className="text-green-100 text-sm">Your signature needs updating</p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <p className="text-sm font-medium text-green-800">{reason || 'A new advertiser has been added to your signature!'}</p>
          </div>

          <p className="text-sm text-gray-600 text-center">
            Head to your Email Signature Builder to copy your updated signature and paste it into your email client.
          </p>

          <Link to="/signatures?blink=copy" className="block">
            <Button className="w-full bg-green-600 hover:bg-green-700 text-white h-11 text-sm font-semibold" onClick={onUpdate} data-testid="go-update-signature-btn">
              <Rocket className="w-4 h-4 mr-2" />
              Update My Signature Now
            </Button>
          </Link>

          <Button variant="ghost" size="sm" className="w-full text-xs text-gray-500" onClick={onDismiss} data-testid="signature-update-later-btn">
            I'll do it later
          </Button>
        </div>
      </div>
    </div>
  );
};


// ==================== STATS HEADER ====================
const StatsHeader = ({ user, stats, connections, onInvitePro }) => {
  const founding = useFoundingStatus();
  return (
    <div className="flex items-center justify-between py-3 px-4 border-b border-border/50 bg-muted/20 rounded-t-lg" data-testid="stats-header">
      <div className="flex items-center gap-3">
        <Link to="/card/me">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary/60 to-primary flex items-center justify-center overflow-hidden border-2 border-primary/30">
            {user?.profile_photo_url ? (
              <img src={resolveImageUrl(user.profile_photo_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white font-bold text-lg">{(user?.full_name || 'U')[0]}</span>
            )}
          </div>
        </Link>
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to="/card/me" className="text-sm font-semibold hover:text-primary transition-colors">{user?.full_name}</Link>
            {founding?.is_founding_member && (
              <Badge
                className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-[9px] px-1.5 py-0 h-4 border border-amber-300 shadow-sm gap-0.5"
                data-testid="stats-founding-pill"
                title="Founding Member — one of the first 1,000"
              >
                <Trophy className="w-2.5 h-2.5" />
                Founding
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{user?.company || user?.profession_name}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3 shadow-sm"
          onClick={onInvitePro}
          data-testid="invite-pro-btn"
        >
          <UserPlus className="w-3.5 h-3.5 mr-1.5" />
          Invite a Pro
        </Button>
        <Link to="/community" className="text-center hover:opacity-80 transition-opacity" data-testid="connections-stat">
          <p className="text-lg font-bold">{connections?.accepted?.length || 0}</p>
          <p className="text-[10px] text-muted-foreground">Connections</p>
        </Link>
        <Link to="/explore" className="text-center hover:opacity-80 transition-opacity" data-testid="referral-stat">
          <p className="text-lg font-bold">{stats?.referral_opportunities ?? '...'}</p>
          <p className="text-[10px] text-muted-foreground">Referral Opportunities</p>
        </Link>
      </div>
    </div>
  );
};

// ==================== JOB APPLICATION MODAL ====================
const JobApplicationModal = ({ job, onClose, onApplied }) => {
  const [answers, setAnswers] = useState(
    (job.screening_questions || []).map(() => '')
  );
  const [resumeFile, setResumeFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleAnswerChange = (idx, val) => {
    const updated = [...answers];
    updated[idx] = val;
    setAnswers(updated);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a PDF, DOC, or DOCX file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }
    setResumeFile(file);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let resumeUrl = '';
      if (resumeFile) {
        setUploading(true);
        const uploadRes = await api.uploadDocument(resumeFile);
        resumeUrl = uploadRes.url || '';
        setUploading(false);
      }
      const formData = new FormData();
      formData.append('answers', JSON.stringify(answers.filter(a => a.trim())));
      if (resumeUrl) formData.append('resume_url', resumeUrl);
      await api.submitApplication(job.id, formData);
      onApplied();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <Card className="w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="job-application-modal">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold">{job.title}</h2>
              <p className="text-sm text-muted-foreground">{job.business_name}{job.location ? ` · ${job.location}` : ''}</p>
            </div>
            <Button variant="ghost" size="sm" className="p-1" onClick={onClose} data-testid="close-apply-modal">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {job.description && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">About this role</p>
              <p className="text-sm whitespace-pre-line">{job.description}</p>
            </div>
          )}

          {job.screening_questions?.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold">Screening Questions</p>
              {job.screening_questions.map((q, i) => (
                <div key={i}>
                  <label className="text-sm text-muted-foreground mb-1 block">Q{i + 1}: {q}</label>
                  <Textarea value={answers[i]} onChange={e => handleAnswerChange(i, e.target.value)}
                    placeholder="Your answer..." className="min-h-[60px] text-sm"
                    data-testid={`apply-answer-${i}`} />
                </div>
              ))}
            </div>
          )}

          {/* Resume Upload */}
          <div>
            <p className="text-sm font-semibold mb-2">Resume (optional)</p>
            <input type="file" ref={fileInputRef} className="hidden"
              accept=".pdf,.doc,.docx" onChange={handleFileSelect} data-testid="resume-file-input" />
            {resumeFile ? (
              <div className="flex items-center gap-2 p-2.5 bg-muted/50 rounded-lg">
                <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm truncate flex-1">{resumeFile.name}</span>
                <Button variant="ghost" size="sm" className="p-1 text-red-500 h-auto"
                  onClick={() => { setResumeFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" className="text-xs w-full"
                onClick={() => fileInputRef.current?.click()} data-testid="upload-resume-btn">
                <Upload className="w-3.5 h-3.5 mr-1.5" />Upload PDF, DOC, or DOCX
              </Button>
            )}
          </div>

          <Button className="w-full h-10" onClick={handleSubmit}
            disabled={submitting} data-testid="submit-application-btn">
            {uploading ? 'Uploading resume...' : submitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== MAIN DASHBOARD ====================
export const Dashboard = () => {
  const { user, refreshProfile } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [connections, setConnections] = useState({ accepted: [], pending_sent: [], pending_received: [] });
  const [recommended, setRecommended] = useState([]);
  const [profileViews, setProfileViews] = useState(null);
  const [showPaymentPlans, setShowPaymentPlans] = useState(false);
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showContactImport, setShowContactImport] = useState(false);
  const [badgesData, setBadgesData] = useState([]);
  const [showInvitePro, setShowInvitePro] = useState(false);
  const [showFoundingPopup, setShowFoundingPopup] = useState(false);
  const [showActivationPopup, setShowActivationPopup] = useState(false);
  const [showSigUpdatePopup, setShowSigUpdatePopup] = useState(false);
  const [sigUpdateReason, setSigUpdateReason] = useState('');
  const [recruitingAds, setRecruitingAds] = useState([]);
  const [jobPosts, setJobPosts] = useState([]);
  const [applyingJob, setApplyingJob] = useState(null);
  const [foundingStatus, setFoundingStatus] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsData, feedData, eventsData, groupsData, connData, recData, viewsData, badgeRes, recruitAdRes, jobFeedRes] = await Promise.all([
        api.getDashboardStats(),
        api.getFeed(1).catch(() => ({ posts: [] })),
        api.getEvents().catch(() => ({ events: [] })),
        api.getGroups().catch(() => ({ groups: [] })),
        api.getConnections().catch(() => ({ accepted: [], pending_sent: [], pending_received: [] })),
        api.getRecommendedMatches().catch(() => ({ recommended: [] })),
        api.getMyProfileViews().catch(() => null),
        api.getMyBadges().catch(() => ({ badges: [] })),
        api.getRecruitingAdsFeed().catch(() => ({ featured: [], regular: [] })),
        api.getJobFeed().catch(() => ({ posts: [] })),
      ]);
      setStats(statsData);
      setPosts(feedData.posts || []);
      setEvents(eventsData.events || []);
      setGroups(groupsData.groups || []);
      setConnections(connData);
      setRecommended(recData.recommended || []);
      setProfileViews(viewsData);
      setBadgesData(badgeRes.badges || []);
      setRecruitingAds([...(recruitAdRes.featured || []), ...(recruitAdRes.regular || [])].slice(0, 3));
      setJobPosts(jobFeedRes.posts || []);
      // Fetch founding member status
      try {
        const token = localStorage.getItem('connectmail_token');
        const fRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/founding-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (fRes.ok) setFoundingStatus(await fRes.json());
      } catch(e) { console.error('founding status fetch failed', e); }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    refreshProfile();
  }, [fetchData, refreshProfile]);

  // Show on-login popups after dashboard data loads
  useEffect(() => {
    if (loading || !user) return;
    const sessionKey = 'connectmail_popup_shown_session';
    const alreadyShownThisSession = sessionStorage.getItem(sessionKey);
    if (alreadyShownThisSession) return;
    sessionStorage.setItem(sessionKey, 'true');

    // Founding Member popup: show if not permanently dismissed and not yet a founding member
    const hideFoundingPopup = localStorage.getItem('connectmail_hide_founding_popup');
    if (!hideFoundingPopup) {
      setTimeout(() => setShowFoundingPopup(true), 1500);
      return;
    }

  }, [loading, user]);

  // No secondary popup needed
  const prevImportOpen = useRef(false);
  useEffect(() => {
    prevImportOpen.current = showFoundingPopup;
  }, [showFoundingPopup, user]);

  // Check for pending signature update notification
  useEffect(() => {
    if (!user) return;
    api.checkSignatureUpdate().then(data => {
      if (data.pending) {
        setSigUpdateReason(data.reason);
        setTimeout(() => setShowSigUpdatePopup(true), 2000);
      }
    }).catch(() => {});
  }, [user]);

  const handleDismissSigUpdate = () => {
    setShowSigUpdatePopup(false);
    api.dismissSignatureUpdate().catch(() => {});
  };

  const handleCreatePost = async (content, postType) => {
    const newPost = await api.createFeedPost(content, postType);
    setPosts([newPost, ...posts]);
  };

  const handleLikePost = async (postId) => {
    try {
      const { liked } = await api.toggleLikePost(postId);
      setPosts(posts.map(p => p.id === postId ? {
        ...p,
        likes: liked ? [...(p.likes || []), user?.id] : (p.likes || []).filter(l => l !== user?.id)
      } : p));
    } catch {}
  };

  const handleDeletePost = async (postId) => {
    try {
      await api.deleteFeedPost(postId);
      setPosts(posts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleCommentAdded = (postId) => {
    setPosts(posts.map(p => p.id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p));
  };

  const handleStartApply = async (job) => {
    try {
      const res = await api.applyToJob(job.id);
      setApplyingJob({ ...job, screening_questions: res.screening_questions || [] });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Unable to apply';
      toast.error(msg);
    }
  };

  const handleUnlockRecommendation = (rec) => {
    window.location.href = `/card/${rec.owner?.id || rec.id}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-16 bg-muted rounded-lg" />
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_280px] gap-6">
            <div className="h-96 bg-muted rounded-lg" />
            <div className="h-96 bg-muted rounded-lg" />
            <div className="h-96 bg-muted rounded-lg" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4" data-testid="dashboard">
        {/* Stats Header */}
        <Card className="overflow-hidden">
          <StatsHeader user={user} stats={stats} connections={connections} onInvitePro={() => setShowInvitePro(true)} />
        </Card>

        {/* Founding Member Banner — celebration view (auto-granted to first 1,000) */}
        {foundingStatus?.is_founding_member && (
          <div
            className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-r from-amber-500/15 via-yellow-400/10 to-amber-500/15 border border-amber-400/40 shadow-sm"
            data-testid="founding-member-badge"
          >
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(251,191,36,0.18),_transparent_55%)] pointer-events-none" />
            <div className="relative flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg ring-2 ring-amber-200">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-extrabold text-amber-900 font-['Outfit']" data-testid="founding-member-title">🏆 Founding Member</p>
                    {foundingStatus.founding_member_number && (
                      <Badge className="bg-amber-100 text-amber-800 text-[10px] border border-amber-300" data-testid="founding-member-number-badge">
                        #{foundingStatus.founding_member_number} of 1,000
                      </Badge>
                    )}
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px] border border-emerald-300">Free Access for Life</Badge>
                  </div>
                  <p className="text-xs text-amber-900/80 mt-0.5">
                    One of the first 1,000 members. Premium access included for life — no subscription required.
                  </p>
                </div>
              </div>
              <Link to="/signatures">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="founding-use-premium-btn">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Use premium templates
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Founding Window Closed Notice — shown only for non-founding users (i.e. user #1001+) */}
        {foundingStatus && !foundingStatus.is_founding_member && (
          <div
            className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-r from-slate-500/5 to-slate-500/10 border border-slate-300"
            data-testid="founding-closed-banner"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">Founding Member program is now closed</p>
                  <p className="text-xs text-slate-500">All 1,000 founding seats have been filled. Upgrade to Pro to access premium features.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main 3-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_280px] gap-6">
          {/* Left Column - Quick Links & Groups */}
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <ProfileTracker user={user} profileViews={profileViews} />
                <QuickLinks user={user} onFeedback={() => setShowFeedback(true)} />
                <MyGroupsWidget groups={groups} />
              </CardContent>
            </Card>
          </div>

          {/* Center Column - Feed & Networking Coach */}
          <div className="space-y-4">
            <NetworkingCoach
              user={user}
              recommendations={recommended}
              onUnlock={handleUnlockRecommendation}
              onEditGoal={() => setShowCoachModal(true)}
            />
            
            <OnboardingChecklist user={user} stats={stats} />

            <PostComposer user={user} onPost={handleCreatePost} />

            {/* Recruiting Ads in Feed */}
            {recruitingAds.length > 0 && (
              <Card className="border-l-4 border-l-blue-500" data-testid="feed-recruiting-ads">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Megaphone className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-semibold">Businesses Hiring Near You</span>
                    </div>
                    <Link to="/business" className="text-xs text-primary hover:underline flex items-center gap-1">
                      View All <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="space-y-2.5">
                    {recruitingAds.map(ad => (
                      <div key={ad.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`feed-ad-${ad.id}`}>
                        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{ad.title}</p>
                          <p className="text-xs text-muted-foreground">{ad.business_name}{ad.city ? ` - ${ad.city}, ${ad.state}` : ''}</p>
                        </div>
                        {ad.cta_url && (
                          <a href={ad.cta_url.startsWith('http') ? ad.cta_url : `https://${ad.cta_url}`} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex-shrink-0">
                            {ad.cta_text || 'Apply'}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Job Posts Feed */}
            {jobPosts.length > 0 && (
              <Card className="border-l-4 border-l-green-500" data-testid="feed-job-posts">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold">Open Positions</span>
                    </div>
                    <Link to="/business" className="text-xs text-primary hover:underline flex items-center gap-1">
                      Browse All <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                  <div className="space-y-2.5">
                    {jobPosts.slice(0, 5).map(job => (
                      <div key={job.id} className="p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`feed-job-${job.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {job.business_logo ? (
                                <img src={resolveImageUrl(job.business_logo)} alt="" className="w-full h-full object-cover rounded-lg" />
                              ) : (
                                <Briefcase className="w-4 h-4 text-green-600" />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{job.title}</p>
                              <p className="text-xs text-muted-foreground">{job.business_name}{job.location ? ` · ${job.location}` : ''}</p>
                              {job.salary_range && <p className="text-xs text-green-700 mt-0.5">{job.salary_range}</p>}
                            </div>
                          </div>
                          <Button size="sm" className="text-xs h-7 flex-shrink-0"
                            onClick={() => handleStartApply(job)}
                            data-testid={`apply-job-${job.id}`}>
                            Apply
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Job Application Modal */}
            {applyingJob && (
              <JobApplicationModal
                job={applyingJob}
                onClose={() => setApplyingJob(null)}
                onApplied={() => {
                  setApplyingJob(null);
                  toast.success('Application submitted!');
                }}
              />
            )}
            {posts.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">No posts yet. Be the first to share something!</p>
                </CardContent>
              </Card>
            ) : (
              posts.slice(0, 10).map(post => (
                <FeedPostCard
                  key={post.id}
                  post={post}
                  user={user}
                  onLike={handleLikePost}
                  onComment={handleCommentAdded}
                  onDelete={handleDeletePost}
                />
              ))
            )}

            {/* Upcoming Event */}
            {events.length > 0 && (
              <EventCard event={events[0]} />
            )}

            {posts.length > 10 && (
              <div className="text-center">
                <Link to="/community">
                  <Button variant="outline" data-testid="view-all-posts">
                    View All Posts
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Right Column - Signature CTA, Stats & Activity */}
          <div className="space-y-4">
            {/* Ad Placement / Signature CTA — primary conversion goal */}
            <AdPlacementPreview user={user} />

            {/* Grow Your Network - Contact Import CTA */}
            <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50" data-testid="grow-network-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <Rocket className="w-4 h-4 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-violet-900">Grow Your Network</p>
                    <p className="text-[10px] text-violet-700">Your signature works best with 3+ partners</p>
                  </div>
                </div>
                <p className="text-xs text-violet-800 mb-3">Import your contacts and earn <strong>1 free month</strong> for each referral that joins!</p>
                <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700 text-xs" onClick={() => setShowContactImport(true)} data-testid="import-contacts-btn">
                  <UserPlus className="w-3.5 h-3.5 mr-1.5" />Import Contacts & Invite
                </Button>
              </CardContent>
            </Card>

            {/* Business Marketplace CTA */}
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="business-marketplace-cta">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Store className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Business Marketplace</p>
                    <p className="text-[10px] text-blue-700">Find partners & job opportunities</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-xs" onClick={() => setShowInvitePro(true)} data-testid="invite-business-owner-btn">
                    <UserPlus className="w-3.5 h-3.5 mr-1.5" />Invite Business Owner
                  </Button>
                  <Link to="/business">
                    <Button size="sm" variant="outline" className="w-full text-xs" data-testid="browse-business-btn">
                      <Briefcase className="w-3.5 h-3.5 mr-1.5" />Browse Businesses
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Earnings Card */}
            <Card data-testid="earnings-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-muted-foreground">Total Earnings</p>
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-green-600" />
                  </div>
                </div>
                <p className="text-2xl font-bold">${stats?.total_earnings?.toFixed(2) || '0.00'}</p>
                <p className="text-xs text-muted-foreground">50% revenue from ad placements</p>
                <Link to="/analytics" className="text-xs text-primary hover:underline flex items-center gap-1 mt-2">
                  <TrendingUp className="w-3 h-3" />
                  View Analytics
                </Link>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-3">
              <Card data-testid="pending-card">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="w-4 h-4 text-amber-500" />
                    <span className="text-lg font-bold">{stats?.pending_approvals || 0}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Pending Approvals</p>
                </CardContent>
              </Card>
              <Card data-testid="views-card">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-blue-500" />
                    <span className="text-lg font-bold">{profileViews?.total_7d || 0}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Profile Views</p>
                </CardContent>
              </Card>
            </div>

            {/* Connection Requests with Accept/Decline */}
            {connections.pending_received?.length > 0 && (
              <Card data-testid="connection-requests">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-primary" />
                    Connection Requests ({connections.pending_received.length})
                  </h3>
                  <div className="space-y-3">
                    {connections.pending_received.slice(0, 3).map((conn) => {
                      const linkTarget = (conn.user_id || conn.sender_id)
                        ? `/card/${conn.user_id || conn.sender_id}`
                        : '/network';
                      return (
                      <div key={conn.connection_id} className="p-3 rounded-lg bg-muted/30 border border-border/30">
                        <div className="flex items-center gap-3 mb-2">
                          <Link to={linkTarget}>
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
                              {conn.photo ? (
                                <img src={resolveImageUrl(conn.photo)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-sm font-bold text-primary">{(conn.name || 'U')[0]}</span>
                              )}
                            </div>
                          </Link>
                          <div className="flex-1 min-w-0">
                            <Link to={linkTarget} className="hover:text-primary">
                              <p className="text-xs font-semibold truncate">{conn.name}</p>
                            </Link>
                            <p className="text-[10px] text-muted-foreground truncate">{conn.profession}</p>
                            {conn.company && <p className="text-[10px] text-muted-foreground/70 truncate">{conn.company}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700"
                            onClick={async () => {
                              try {
                                await api.acceptConnectionRequest(conn.connection_id || conn.id);
                                toast.success('Connection accepted!');
                                fetchData();
                              } catch (e) {
                                toast.error('Failed to accept');
                              }
                            }}
                            data-testid={`accept-${conn.connection_id || conn.id}`}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 h-7 text-xs"
                            onClick={async () => {
                              try {
                                await api.declineConnectionRequest(conn.connection_id || conn.id);
                                toast.success('Request declined');
                                fetchData();
                              } catch (e) {
                                toast.error('Failed to decline');
                              }
                            }}
                            data-testid={`decline-${conn.connection_id || conn.id}`}
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  {connections.pending_received.length > 3 && (
                    <Link to="/community" className="text-xs text-primary hover:underline flex items-center gap-1 mt-3">
                      View All ({connections.pending_received.length})
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Upcoming Events */}
            {events.length > 1 && (
              <Card data-testid="upcoming-events">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    Upcoming Events
                  </h3>
                  <div className="space-y-2">
                    {events.slice(1, 4).map((event) => (
                      <Link
                        key={event.id}
                        to="/events"
                        className="block p-2 rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <p className="text-xs font-medium">{event.title}</p>
                        <p className="text-[10px] text-muted-foreground">{event.event_date} • {event.event_type}</p>
                      </Link>
                    ))}
                  </div>
                  <Link to="/events" className="text-xs text-primary hover:underline flex items-center gap-1 mt-2">
                    View All Events
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Quick Network Actions: Discover | Explore */}
            <QuickNetworkHub user={user} recommended={recommended} />

            {/* Badges & Leaderboard Preview */}
            <Card data-testid="badges-widget">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold flex items-center gap-1.5">
                    <Award className="w-4 h-4 text-amber-500" />
                    Your Badges
                  </h3>
                  <Link to="/leaderboard" className="text-xs text-primary hover:underline">View All</Link>
                </div>
                <div className="flex gap-2 flex-wrap mb-3">
                  {(badgesData || []).slice(0, 4).map(b => (
                    <div key={b.id} className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center" title={b.name}>
                      <Award className="w-4 h-4 text-primary" />
                    </div>
                  ))}
                  {(!badgesData || badgesData.length === 0) && (
                    <p className="text-xs text-muted-foreground">Start networking to earn badges!</p>
                  )}
                </div>
                <Link to="/leaderboard">
                  <Button size="sm" variant="outline" className="w-full text-xs h-7" data-testid="view-leaderboard-btn">
                    <Award className="w-3 h-3 mr-1" /> View Leaderboard & Badges
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Networking Coach Modal */}
        {showCoachModal && (
          <NetworkingCoachModal
            user={user}
            recommended={recommended}
            onClose={() => setShowCoachModal(false)}
          />
        )}

        {/* Payment Plans Modal */}
        {showPaymentPlans && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowPaymentPlans(false)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 relative" onClick={e => e.stopPropagation()} data-testid="payment-plans-modal">
              <button onClick={() => setShowPaymentPlans(false)} className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold font-['Outfit']">Choose Your Plan</h3>
                <p className="text-muted-foreground text-sm mt-1">$20/month with auto-pay. Or pay in advance.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { type: 'subscription_1', label: 'Monthly', price: '$20', sub: '/month', highlight: false },
                  { type: 'subscription_3', label: '3 Months', price: '$60', sub: 'Pay in advance', highlight: false },
                  { type: 'subscription_6', label: '6 Months', price: '$120', sub: 'Pay in advance', highlight: true },
                  { type: 'subscription_12', label: '12 Months', price: '$240', sub: 'Pay in advance', highlight: false }
                ].map((plan) => (
                  <button
                    key={plan.type}
                    onClick={async () => {
                      try {
                        const checkout = await api.createCheckout(plan.type);
                        if (checkout.url) window.location.href = checkout.url;
                      } catch { toast.error('Failed to start checkout'); }
                    }}
                    className={`rounded-xl p-5 text-center transition-all hover:scale-105 border-2 ${plan.highlight ? 'border-primary bg-primary/5 shadow-lg' : 'border-gray-200 hover:border-primary/50'}`}
                    data-testid={`plan-${plan.type}`}
                  >
                    {plan.highlight && <span className="text-[10px] bg-primary text-white px-2 py-0.5 rounded-full font-bold uppercase">Popular</span>}
                    <p className="text-3xl font-bold font-['Outfit'] mt-2">{plan.price}</p>
                    <p className="text-sm font-semibold text-primary mt-1">{plan.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{plan.sub}</p>
                  </button>
                ))}
              </div>
              <p className="text-center text-xs text-muted-foreground mt-4">
                All plans include 2 ad slots. Open up to 5 total for free. Earn $10 per filled slot.
              </p>
              {window.location.hostname.includes('preview.emergentagent.com') && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-white h-11 text-sm font-semibold"
                    data-testid="test-activate-plans-btn"
                    onClick={async () => {
                      try {
                        const result = await api.testActivate();
                        if (result.session_id) {
                          toast.success('Test activation successful! Your account is now premium.');
                          setShowPaymentPlans(false);
                          refreshProfile();
                        }
                      } catch { toast.error('Test activation failed'); }
                    }}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Test Activate (No Charge — Preview Only)
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} />
      {showContactImport && <ContactImportModal onClose={() => setShowContactImport(false)} onComplete={() => setShowContactImport(false)} />}
      {showInvitePro && <InviteProModal onClose={() => setShowInvitePro(false)} />}
      {showFoundingPopup && (
        <FoundingMemberPopup
          onClose={() => setShowFoundingPopup(false)}
          onInvite={() => setShowInvitePro(true)}
        />
      )}
      {showSigUpdatePopup && (
        <SignatureUpdatePopup
          reason={sigUpdateReason}
          onUpdate={handleDismissSigUpdate}
          onDismiss={handleDismissSigUpdate}
        />
      )}
    </Layout>
  );
};

export default Dashboard;
